/**
 * One record.
 *
 * A carrier group handles position, tilt and opacity during a swap; a spinner
 * inside it does nothing but rotate. Each record owns the textures it was
 * given, so a swap can dispose the outgoing one without touching the shared
 * groove maps.
 */

import * as THREE from 'three';
import { DIM } from '../geometry/dims.ts';
import type { Palette } from '../lib/palette.ts';
import { labelTexture, pictureDiscTexture } from '../textures/label.ts';
import { vinylNormalMap, vinylRoughnessMap, vinylSurfaceMap } from '../textures/vinyl.ts';
import { cyl } from './shapes.ts';

const HOLE_R = 0.38;
const SEGMENTS = 192;

export interface RecordMesh {
  carrier: THREE.Group;
  spinner: THREE.Group;
  setArtwork(art: CanvasImageSource | null, palette: Palette | null, pictureDisc?: boolean): void;
  setOpacity(o: number): void;
  dispose(): void;
}

function vinylMaterial(renderer: THREE.WebGLRenderer): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    map: vinylSurfaceMap(1024, renderer),
    normalMap: vinylNormalMap(1024, renderer),
    normalScale: new THREE.Vector2(0.55, 0.55),
    roughnessMap: vinylRoughnessMap(512, renderer),
    roughness: 1,
    metalness: 0,
    /* Black vinyl is a mirror for a bright studio environment. Dialled well
       down so the disc stays black instead of reflecting the room to grey. */
    clearcoat: 0.45,
    clearcoatRoughness: 0.25,
    specularIntensity: 0.5,
    reflectivity: 0.35,
    envMapIntensity: 0.22,
  });
}

export function createRecord(renderer: THREE.WebGLRenderer): RecordMesh {
  const carrier = new THREE.Group();
  const spinner = new THREE.Group();
  carrier.add(spinner);

  const R = DIM.recordR;
  const H = DIM.recordH;

  const vinylMat = vinylMaterial(renderer);
  const edgeMat = new THREE.MeshPhysicalMaterial({
    color: 0x0d0d10,
    roughness: 0.32,
    metalness: 0,
    clearcoat: 0.6,
    clearcoatRoughness: 0.2,
    envMapIntensity: 0.3,
  });
  const labelMat = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    roughness: 0.58,
    metalness: 0,
    clearcoat: 0.2,
    clearcoatRoughness: 0.5,
    alphaTest: 0.5,
    side: THREE.FrontSide,
  });

  const top = new THREE.Mesh(new THREE.RingGeometry(HOLE_R, R, SEGMENTS, 1), vinylMat);
  top.rotation.x = -Math.PI / 2;
  top.position.y = H / 2;
  top.castShadow = true;
  top.receiveShadow = true;

  const bottom = new THREE.Mesh(new THREE.RingGeometry(HOLE_R, R, SEGMENTS, 1), edgeMat);
  bottom.rotation.x = Math.PI / 2;
  bottom.position.y = -H / 2;

  const outer = new THREE.Mesh(cyl(R, R, H, SEGMENTS, true), edgeMat);
  const inner = new THREE.Mesh(
    cyl(HOLE_R, HOLE_R, H + 0.02, 32, true),
    new THREE.MeshStandardMaterial({ color: 0x14141a, roughness: 0.6, side: THREE.BackSide }),
  );

  const label = new THREE.Mesh(new THREE.CircleGeometry(DIM.labelR, 96), labelMat);
  label.rotation.x = -Math.PI / 2;
  label.position.y = H / 2 + 0.012;

  const labelBack = new THREE.Mesh(new THREE.CircleGeometry(DIM.labelR, 64), labelMat);
  labelBack.rotation.x = Math.PI / 2;
  labelBack.position.y = -H / 2 - 0.012;

  spinner.add(top, bottom, outer, inner, label, labelBack);

  let owned: THREE.Texture[] = [];

  const tintFrom = (palette: Palette | null, pictureDisc: boolean): void => {
    if (palette?.dominant) {
      /* Let the artwork bleed into the vinyl itself — a hint of coloured wax. */
      vinylMat.color.set('#ffffff').lerp(new THREE.Color(palette.dominant), pictureDisc ? 0 : 0.22);
      edgeMat.color.set(0x0d0d10).lerp(new THREE.Color(palette.deep || palette.dominant), 0.5);
    } else {
      vinylMat.color.set(0xffffff);
      edgeMat.color.set(0x0d0d10);
    }
  };

  return {
    carrier,
    spinner,

    setArtwork(art, palette, pictureDisc = false) {
      const old = owned;
      owned = [];

      if (pictureDisc) {
        const t = pictureDiscTexture(art, { palette });
        owned.push(t);
        vinylMat.map = t;
        vinylMat.normalScale.set(0.45, 0.45);
        vinylMat.clearcoat = 0.35;
        vinylMat.envMapIntensity = 0.3;
        label.visible = false;
        labelBack.visible = false;
      } else {
        vinylMat.map = vinylSurfaceMap(1024, renderer);
        vinylMat.normalScale.set(0.55, 0.55);
        vinylMat.clearcoat = 0.45;
        vinylMat.envMapIntensity = 0.22;
        label.visible = true;
        labelBack.visible = true;
        const t = labelTexture(art, { palette });
        owned.push(t);
        labelMat.map = t;
        labelMat.needsUpdate = true;
      }
      vinylMat.needsUpdate = true;
      tintFrom(palette, pictureDisc);
      for (const t of old) t.dispose();
    },

    setOpacity(o) {
      const translucent = o < 0.999;
      for (const m of [vinylMat, edgeMat, labelMat]) {
        m.transparent = translucent || m === labelMat;
        m.opacity = o;
        m.depthWrite = o > 0.5;
        m.needsUpdate = true;
      }
    },

    dispose() {
      for (const t of owned) t.dispose();
      for (const mesh of [top, bottom, outer, inner, label, labelBack]) mesh.geometry.dispose();
      for (const m of [vinylMat, edgeMat, labelMat]) m.dispose();
    },
  };
}
